import React, { useRef, useEffect } from 'react';
import { Chat } from '../types';
import { chatService } from '../services/chatService';
import { getOrderStatusInfo } from '../utils/statusUtils';
import { useSafeContent } from '../utils/sanitizer';

interface ChatListProps {
  chats: Chat[];
  isLoading: boolean;
  onChatSelect: (chat: Chat) => void;
  onLoadMore: (page: number) => void;
  className?: string;
}

export const ChatList: React.FC<ChatListProps> = ({
  chats,
  isLoading,
  onChatSelect,
  onLoadMore,
  className = ''
}) => {
  const { sanitizePreview } = useSafeContent();
  const chatListRef = useRef<HTMLDivElement>(null);

  // Старая логика удалена - используем правильный подход с CSS
  
  /**
   * Форматирование времени для чата
   */
  const formatChatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'сейчас';
    if (diffMinutes < 60) return `${diffMinutes}м`;
    if (diffHours < 24) return `${diffHours}ч`;
    if (diffDays < 7) return `${diffDays}д`;
    
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  };

  /**
   * Получение аббревиатуры для аватара
   */
  const getAbbreviation = (title: string): string => {
    return title
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };



  if (isLoading && chats.length === 0) {
    return (
      <div className={`chat-widget-loading ${className}`}>
        <div className="loading-spinner"></div>
        <p>Загрузка чатов...</p>
        <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
          Получение списка заказов
        </span>
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div className={`chat-widget-empty ${className}`}>
        <div className="empty-icon">💬</div>
        <h4>Нет активных чатов</h4>
        <p>Чаты появятся когда заказы перейдут в стадию "просмотры"</p>
      </div>
    );
  }

  return (
    <div className={`chat-widget-list-container ${className}`}>
      <div className="chat-widget-list" ref={chatListRef}>
        {chats.map((chat) => {
          const statusInfo = getOrderStatusInfo(chat.status);
          
          return (
            <div
              key={chat.id}
              onClick={() => {
                console.log('🔥 КЛИК ПО ЧАТУ:', chat.id, chat.title);
                onChatSelect(chat);
              }}
              className={`chat-widget-item ${chat.unreadCount > 0 ? 'has-unread' : ''} ${chat.chatType === 'telegram_general' ? 'general-chat' : ''}`}
              data-chat-id={chat.id}
            >
              {/* Аватар */}
              <div className={`chat-widget-avatar ${chat.unreadCount > 0 ? 'has-unread' : ''}`}>
                {chat.avatar || getAbbreviation(chat.title)}
                
                              {/* Иконка статуса заказа - НЕ показываем для общего чата */}
              {chat.chatType !== 'telegram_general' && (
                  <div 
                    className="chat-status"
                    style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.textColor }}
                    title={statusInfo.title}
                  >
                    <ion-icon 
                      name={statusInfo.iconName} 
                      style={{ fontSize: '16px' }}
                    ></ion-icon>
                  </div>
                )}
              </div>

              {/* Информация о чате */}
              <div className="chat-widget-info">
                <div className="chat-widget-header-item">
                  <h4 className="chat-widget-title">
                    {chat.title}
                  </h4>
                                  {/* Время - НЕ показываем для общего чата */}
                {chat.chatType !== 'telegram_general' && (
                    <span className="chat-widget-time">
                      {formatChatTime(chat.lastMessageTime)}
                    </span>
                  )}
                </div>
                
                <div className="chat-widget-preview">
                  <p className="chat-widget-last-message">
                    {sanitizePreview(chat.lastMessage, 50)}
                  </p>
                  
                  {/* Счетчик непрочитанных */}
                  {chat.unreadCount > 0 && (
                    <div className="chat-widget-unread">
                      {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                    </div>
                  )}
                </div>

                {/* Дополнительная информация о заказе */}
                {(chat.price_formatted || chat.delivery_time_formatted) && (
                  <div className="flex items-center space-x-3 mt-1">
                    {chat.price_formatted && (
                      <span className="text-xs text-green-600 font-medium">
                        💰 {chat.price_formatted}
                      </span>
                    )}
                    {chat.delivery_time_formatted && (
                      <span className="text-xs text-blue-600 font-medium">
                        ⏱️ {chat.delivery_time_formatted}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Индикатор загрузки внизу */}
      {isLoading && chats.length > 0 && (
        <div className="p-4 text-center border-t border-gray-100 bg-gray-50">
          <div className="loading-spinner" style={{ width: '20px', height: '20px', margin: '0 auto' }}></div>
          <p style={{ fontSize: '12px', color: '#64748b', margin: '8px 0 0 0' }}>
            Загрузка еще...
          </p>
        </div>
      )}
    </div>
  );
};
